import { useEffect, useState } from "react";
import { confirmSignIn, signIn } from "aws-amplify/auth";
import { useNavigate } from "react-router-dom";
import UILoader from "../../_app/components/UILoader";
import { ChangePasswordForm } from "../types";
import { useCognitoUserpoolPolicy } from "../hooks";
import { useQueryParam } from "../../_app/hooks";
import { getSensitiveParam } from "../lib/sensitiveParams";
import { useQueryClient } from "@tanstack/react-query";
import CognitoPasswordChangeForm from "../components/cognito/CognitoPasswordChangeForm";
import { useContextProvider } from "../../context/providers/ContextProvider";
import { useAuthenticator } from "@aws-amplify/ui-react";

const ForcePasswordChange = () => {
  const [isSigning, setIsSigning] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { data: passwordPolicy, isFetching } = useCognitoUserpoolPolicy();
  const navigate = useNavigate();
  // Prefer the stashed value (stripped from the URL at bootstrap); fall back to the query param.
  const usernameParam = useQueryParam("username") as string;
  const passwordParam = useQueryParam("pass") as string;
  const username = getSensitiveParam("username") ?? usernameParam;
  const password = getSensitiveParam("pass") ?? passwordParam;
  const { reset: resetContext } = useContextProvider();
  const { signOut } = useAuthenticator((context) => [context.user]);
  const queryClient = useQueryClient();

  // Sign in user programmatically using the encoded credentials
  useEffect(() => {
    const performSignIn = async () => {
      if (!username || !password) {
        setError("Oops! Invalid link. Try requesting a new one, or reach out to support for help.");
        setIsSigning(false);
        return;
      }

      // Sign out any existing user
      signOut();
      localStorage.clear();

      signIn({ username, password: password })
        .catch(() => {
          setError(
            `Oops! This link isn't working. It may have expired or already been used. Please request a new one or contact support for assistance. The link remains valid for ${passwordPolicy?.temporaryPasswordValidityDays || 7} days.`,
          );
        })
        .finally(() => {
          setIsSigning(false);
        });
    };

    performSignIn();
    // eslint-disable-next-line
  }, [username, password, passwordPolicy?.temporaryPasswordValidityDays]);

  async function handleChangePassword(form: ChangePasswordForm) {
    try {
      const { isSignedIn } = await confirmSignIn({
        challengeResponse: form.password,
      });

      if (isSignedIn) {
        resetContext();

        //Temporary fix. After removing SSO and simplifying auth flow this should not be necessary.
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: ["identityUser"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["hubUserExist"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["myUserType"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["user-permissions"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["myAccounts"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["enabledFeatures"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["mfa-enabled"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["accountHierarchy"],
          }),
          queryClient.invalidateQueries({
            queryKey: ["costCentres"],
          }),
        ]);

        navigate("/");
      }
    } catch (error) {
      setError("Password change was not successful. Please try again or contact support if the issue persists.");
    }
  }

  useEffect(() => {
    if (error) {
      navigate("/auth/feedback", {
        state: {
          title: "Change Password",
          type: "error",
          message: error,
        },
      });
    }
  }, [error, navigate]);

  if (isSigning || isFetching) return <UILoader />;

  return <CognitoPasswordChangeForm heading="Change Password" passwordPolicy={passwordPolicy} onSubmit={handleChangePassword} />;
};

export default ForcePasswordChange;
